<div class="budget-card">
  <!-- Header -->
  <div class="budget-header d-flex justify-content-between align-items-center">
    <h6 class="mb-0">
      <i class="bi bi-calculator me-2"></i>{{ 'Budget.title' | translate }}
    </h6>
    <div class="d-flex gap-2 align-items-center">
      <span class="badge badge-soft-dark" *ngIf="budgetSummary && budgetSummary.coveragePercent != null">
        {{ 'Budget.coverage' | translate }}: {{ budgetSummary.coveragePercent | number:'1.1-1' }}%
      </span>
      <button *ngIf="!editing && !loading" class="btn btn-sm btn-outline-secondary" (click)="startEdit()"
              [attr.aria-label]="'General.edit' | translate">
        <i class="bi bi-pencil"></i>
      </button>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="loading" class="text-center py-4">
    <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
  </div>

  <!-- View mode -->
  <div *ngIf="!loading && !editing" class="budget-table-wrapper">
    <table class="table table-sm budget-table mb-0">
      <thead>
        <tr>
          <th>{{ 'Budget.columnCategory' | translate }}</th>
          <th class="text-end">{{ 'Budget.columnBudget' | translate }}</th>
          <th class="text-end">{{ 'Budget.columnEstHours' | translate }}</th>
          <th class="text-end">{{ 'Budget.columnActual' | translate }}</th>
          <th class="text-end">{{ 'Budget.columnActualHours' | translate }}</th>
          <th class="text-end">{{ 'Budget.columnVariance' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <!-- Income section header -->
        <tr class="section-header">
          <td colspan="6" class="fw-bold text-uppercase small text-muted pt-2">{{ 'Budget.incomeHeader' | translate }}</td>
        </tr>
        <!-- Income lines -->
        <tr *ngFor="let cat of incomeCategories">
          <td>
            <span class="category-label ps-2">{{ getCategoryLabel(cat.key) }}</span>
            <small class="text-muted d-block ps-2" *ngIf="getLine(cat.key)?.notes">{{ getLine(cat.key)?.notes }}</small>
          </td>
          <td class="text-end">{{ formatCurrency(getLine(cat.key)?.budgetAmount) }}</td>
          <td class="text-end">
            <span *ngIf="getLine(cat.key)?.estimatedHours">{{ getLine(cat.key)?.estimatedHours | number:'1.1-1' }}</span>
            <span *ngIf="!getLine(cat.key)?.estimatedHours" class="text-muted">–</span>
          </td>
          <td class="text-end">{{ formatCurrency(getLine(cat.key)?.actualAmount) }}</td>
          <td class="text-end text-muted">–</td>
          <td class="text-end" [class]="getVarianceClass(getLine(cat.key)?.variance ?? 0)">
            {{ formatCurrency(getLine(cat.key)?.variance) }}
          </td>
        </tr>
        <!-- Income subtotal -->
        <tr class="subtotal-row">
          <td class="fw-semibold ps-2">{{ 'Budget.sumIncome' | translate }}</td>
          <td class="text-end fw-semibold">{{ formatCurrency(incomeBudget) }}</td>
          <td></td>
          <td class="text-end fw-semibold">{{ formatCurrency(incomeActual) }}</td>
          <td></td>
          <td></td>
        </tr>

        <!-- Cost section header -->
        <tr class="section-header">
          <td colspan="6" class="fw-bold text-uppercase small text-muted pt-3">{{ 'Budget.costHeader' | translate }}</td>
        </tr>
        <!-- Cost lines -->
        <tr *ngFor="let cat of costCategories">
          <td>
            <span class="category-label ps-2">{{ getCategoryLabel(cat.key) }}</span>
            <small class="text-muted d-block ps-2" *ngIf="getLine(cat.key)?.notes">{{ getLine(cat.key)?.notes }}</small>
          </td>
          <td class="text-end">{{ formatCurrency(getLine(cat.key)?.budgetAmount) }}</td>
          <td class="text-end">
            <span *ngIf="getLine(cat.key)?.estimatedHours">{{ getLine(cat.key)?.estimatedHours | number:'1.1-1' }}</span>
            <span *ngIf="!getLine(cat.key)?.estimatedHours" class="text-muted">–</span>
          </td>
          <td class="text-end">{{ formatCurrency(getLine(cat.key)?.actualAmount) }}</td>
          <td class="text-end">
            <span *ngIf="cat.key === 'Personnel' && getLine(cat.key)?.actualHours">{{ getLine(cat.key)?.actualHours | number:'1.1-1' }}</span>
            <span *ngIf="cat.key !== 'Personnel' || !getLine(cat.key)?.actualHours" class="text-muted">–</span>
          </td>
          <td class="text-end" [class]="getVarianceClass(getLine(cat.key)?.variance ?? 0)">
            {{ formatCurrency(getLine(cat.key)?.variance) }}
          </td>
        </tr>
        <!-- Cost subtotal -->
        <tr class="subtotal-row">
          <td class="fw-semibold ps-2">{{ 'Budget.sumCost' | translate }}</td>
          <td class="text-end fw-semibold">{{ formatCurrency(costBudget) }}</td>
          <td></td>
          <td class="text-end fw-semibold">{{ formatCurrency(costActual) }}</td>
          <td class="text-end">
            <span *ngIf="budgetSummary?.totalActualHours">{{ budgetSummary?.totalActualHours | number:'1.1-1' }}</span>
          </td>
          <td></td>
        </tr>
      </tbody>
      <tfoot>
        <!-- Net margin row (Dekningsbidrag) -->
        <tr class="fw-bold border-top">
          <td>{{ 'Budget.netMargin' | translate }}</td>
          <td class="text-end">{{ formatCurrency(budgetSummary?.totalBudget) }}</td>
          <td class="text-end">
            <span *ngIf="budgetSummary?.totalEstimatedHours">{{ budgetSummary?.totalEstimatedHours | number:'1.1-1' }}</span>
            <span *ngIf="!budgetSummary?.totalEstimatedHours" class="text-muted">–</span>
          </td>
          <td class="text-end">{{ formatCurrency(budgetSummary?.totalActual) }}</td>
          <td></td>
          <td class="text-end" [class]="getVarianceClass(budgetSummary?.totalVariance ?? 0)">
            {{ formatCurrency(budgetSummary?.totalVariance) }}
          </td>
        </tr>
      </tfoot>
    </table>

    <!-- Empty state -->
    <div *ngIf="!budgetSummary || budgetSummary.lines.length === 0" class="text-center text-muted small py-3">
      <i class="bi bi-calculator d-block mb-1" style="font-size: 1.5rem;"></i>
      {{ 'Budget.noBudgetLines' | translate }}
    </div>
  </div>

  <!-- Edit mode -->
  <div *ngIf="editing" class="budget-edit">
    <table class="table table-sm budget-table mb-3">
      <thead>
        <tr>
          <th>{{ 'Budget.columnCategory' | translate }}</th>
          <th class="text-end" style="width: 140px;">{{ 'Budget.columnBudget' | translate }}</th>
          <th class="text-end" style="width: 100px;">{{ 'Budget.columnEstHours' | translate }}</th>
          <th style="width: 200px;">{{ 'Budget.columnNotes' | translate }}</th>
        </tr>
      </thead>
      <tbody>
        <!-- Income section -->
        <tr class="section-header">
          <td colspan="4" class="fw-bold text-uppercase small text-muted pt-2">{{ 'Budget.incomeHeader' | translate }}</td>
        </tr>
        <ng-container *ngFor="let line of editLines; let i = index">
          <tr *ngIf="isIncomeCategory(line.category)">
            <td class="ps-2">{{ getCategoryLabel(line.category) }}</td>
            <td>
              <input type="number" class="form-control form-control-sm text-end"
                     [(ngModel)]="line.budgetAmount" [ngModelOptions]="{ standalone: true }" />
            </td>
            <td>
              <input type="number" class="form-control form-control-sm text-end"
                     [(ngModel)]="line.estimatedHours" [ngModelOptions]="{ standalone: true }"
                     [placeholder]="'Budget.estHoursPlaceholder' | translate" step="0.5" />
            </td>
            <td>
              <input type="text" class="form-control form-control-sm"
                     [(ngModel)]="line.notes" [ngModelOptions]="{ standalone: true }"
                     [placeholder]="'Budget.notesPlaceholder' | translate" />
            </td>
          </tr>
        </ng-container>
        <tr class="subtotal-row">
          <td class="fw-semibold ps-2">{{ 'Budget.sumIncome' | translate }}</td>
          <td class="text-end fw-semibold">{{ formatCurrency(editIncome) }}</td>
          <td></td>
          <td></td>
        </tr>

        <!-- Cost section -->
        <tr class="section-header">
          <td colspan="4" class="fw-bold text-uppercase small text-muted pt-3">{{ 'Budget.costHeader' | translate }}</td>
        </tr>
        <ng-container *ngFor="let line of editLines; let i = index">
          <tr *ngIf="!isIncomeCategory(line.category)">
            <td class="ps-2">{{ getCategoryLabel(line.category) }}</td>
            <td>
              <input type="number" class="form-control form-control-sm text-end"
                     [(ngModel)]="line.budgetAmount" [ngModelOptions]="{ standalone: true }" />
            </td>
            <td>
              <input type="number" class="form-control form-control-sm text-end"
                     [(ngModel)]="line.estimatedHours" [ngModelOptions]="{ standalone: true }"
                     [placeholder]="'Budget.estHoursPlaceholder' | translate" step="0.5" />
            </td>
            <td>
              <input type="text" class="form-control form-control-sm"
                     [(ngModel)]="line.notes" [ngModelOptions]="{ standalone: true }"
                     [placeholder]="'Budget.notesPlaceholder' | translate" />
            </td>
          </tr>
        </ng-container>
        <tr class="subtotal-row">
          <td class="fw-semibold ps-2">{{ 'Budget.sumCost' | translate }}</td>
          <td class="text-end fw-semibold">{{ formatCurrency(editCost) }}</td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="fw-bold border-top">
          <td>{{ 'Budget.netMargin' | translate }}</td>
          <td class="text-end">{{ formatCurrency(editTotal) }}</td>
          <td></td>
          <td></td>
        </tr>
      </tfoot>
    </table>

    <!-- Save / Cancel -->
    <div class="d-flex gap-2 justify-content-end">
      <button class="btn btn-sm btn-dark" (click)="cancelEdit()">{{ 'General.cancel' | translate }}</button>
      <button class="btn btn-sm btn-primary" (click)="save()" [disabled]="saving">
        <span *ngIf="saving" class="spinner-border spinner-border-sm me-1"></span>
        {{ 'General.save' | translate }}
      </button>
    </div>
  </div>
</div>
